import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { IBbitWebShopItemStockResponse } from '@bbitgmbh/bbit.utils';
import { BbitWebShopApiService, BbitWebShopService } from '@bbitgmbh/webshop.sdk';
import { BbitWebsiteService, I18nPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { LoadingComponent } from '../loading/loading.component';

@Component({
  selector: 'app-item-stock',
  templateUrl: './item-stock.component.html',
  styleUrls: ['./item-stock.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LoadingComponent, NgIcon, AsyncPipe, I18nPipe],
})
export class ItemStockComponent implements OnInit, OnDestroy, OnChanges {
  private _api = inject(BbitWebShopApiService);
  private _website = inject(BbitWebsiteService);
  webShop = inject(BbitWebShopService);

  public isLoading = new BehaviorSubject(true);
  public stockSubject = new BehaviorSubject<IBbitWebShopItemStockResponse | undefined>(undefined);
  private _ngUnsubscribe = new Subject<void>();

  readonly item = input<string>();
  readonly hideText = input(false);
  readonly doNotShowLoadingSpinner = input(false);
  @Input()
  set stock(stock: IBbitWebShopItemStockResponse | undefined | null) {
    if (stock) {
      this.stockSubject.next(stock);
    }
  }

  constructor() {
    this._website
      .observeAuthenticated()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(() => {
        this._reload();
      });
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next();
    this._ngUnsubscribe.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    const stockChange = changes['stock'];
    if (stockChange?.currentValue) {
      this.stockSubject.next(stockChange.currentValue);
      return;
    }
    const itemChange = changes['item'];
    if (itemChange?.currentValue !== itemChange?.previousValue) {
      this._reload();
    }
  }

  async ngOnInit() {
    await this._reload();
  }

  private async _reload() {
    const item = this.item();
    if (item) {
      this.isLoading.next(true);
      try {
        const result = await this._api.getItemPriceAndStock(item);
        this.stockSubject.next(result.stock);
      } catch (err) {
        console.error('Error while loading stock', item, err);
        this.stockSubject.next(undefined);
      }
    }
    this.isLoading.next(false);
  }
}
